<template>
    <v-data-table
        v-model="selected"
        :class="$options.name"
        :headers="headers"
        :items="items"
        :items-per-page="itemsPerPage"
        :loading="loading"
        :show-select="showSelect"
        :server-items-length="serverItemsLength"
        :sort-by="sort.column"
        :sort-desc="sort.direction == 'desc'"
        :disable-sort="disableSort"
        :item-key="itemKey"
        :mobile-breakpoint="1"
        :dense="dense"
        :must-sort="mustSort"
        :selectable-key="selectableKey"
        hide-default-header
        hide-default-footer
        class="elevation-1"
        @update:sort-by="updateSortBy"
        @update:sort-desc="updateSortDesc"
        @click:row="onClickRow"
    >
        <template v-slot:header="{ props, on }">
            <v-data-table-header
                :headers="props.headers"
                :options="props.options"
                :some-items="props.someItems"
                :every-item="props.everyItem"
                :mobile="props.mobile"
                :show-group-by="props.showGroupBy"
                :single-select="props.singleSelect"
                :disable-sort="props.disableSort"
                :class="`${$options.name}__TableHeader`"
                class="secondary elevation-4"
                @sort="on.sort"
                @group="on.group"
                @toggle-select-all="on['toggle-select-all']"
            >
                <template v-slot:data-table-select="{ props: headerProps, on: headerOn }">
                    <v-toolbar v-if="hasHeaderMenuButtonSlot" dense flat class="transparent">
                        <v-btn-toggle class="transparent" mandatory>
                            <v-simple-checkbox
                                :value="headerProps.value"
                                :indeterminate="headerProps.indeterminate"
                                :disabled="disableSelect"
                                color="accent"
                                @input="headerOn.input"
                            />
                            <slot name="header-menu-button" />
                        </v-btn-toggle>
                    </v-toolbar>
                    <v-simple-checkbox
                        v-else
                        :value="headerProps.value"
                        :indeterminate="headerProps.indeterminate"
                        :disabled="disableSelect"
                        color="accent"
                        @input="headerOn.input"
                    />
                </template>
                <template v-for="key in headerColSlotNames" v-slot:[key]="{ header }">
                    <slot :name="`header.${key}`" :header="header" />
                </template>
            </v-data-table-header>
        </template>
        <template v-slot:progress>
            <v-progress-linear ref="progress-bar" color="accent" indeterminate />
        </template>
        <template v-if="hasItemSlot" v-slot:item="{ item, select, isSelected, headers, index }">
            <slot
                :item="item"
                :select="select"
                :is-selected="isSelected"
                :headers="headers"
                :index="index"
                name="item"
            />
        </template>
        <template v-if="!hasItemSlot" v-slot:item.data-table-select="{ item, isSelected, select }">
            <div :class="{ 'pl-0': hasHeaderMenuButtonSlot }">
                <v-simple-checkbox
                    :value="isSelected"
                    :disabled="disableSelect || !isItemSelectable(item)"
                    color="accent"
                    @input="select"
                />
            </div>
        </template>
        <template v-for="key in itemColSlotNames" v-slot:[key]="{ item, header, value }">
            <slot v-if="!hasItemSlot" :name="key" :item="item" :header="header" :value="value" />
        </template>
        <template v-slot:no-data>
            <table-item-not-found-alert v-if="!loading" :message="notFoundMessage" />
            <span v-else key="now-loading" />
        </template>
    </v-data-table>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableItemNotFoundAlert from './TableItemNotFoundAlert.vue';
import SortParam from '../../utils/types/SortParam';

@Component({
    name: 'DataTable',
    components: {
        TableItemNotFoundAlert,
    },
})
export default class DataTable extends Vue {
    @Prop({ default: () => [] }) readonly value: Array<any>;
    @Prop({ default: undefined }) readonly headers?: Array<any>;
    @Prop({ default: () => [] }) readonly items: Array<any>;
    @Prop({ default: undefined }) readonly loading?: boolean;
    @Prop({ default: undefined }) readonly showSelect?: boolean;
    @Prop({ default: undefined }) readonly serverItemsLength?: number;
    @Prop({ default: undefined }) readonly disableSort?: boolean;
    @Prop({ default: false }) readonly disableSelect: boolean;
    @Prop({ default: () => new SortParam<any>() }) readonly sort: SortParam<any>;
    @Prop({ default: 'id' }) readonly itemKey: string;
    @Prop({ default: true }) readonly dense: boolean;
    @Prop({ default: 'isSelectable' }) readonly selectableKey: string;
    @Prop({ default: true }) readonly mustSort: boolean;
    @Prop({ default: '' }) readonly notFoundMessage: string;
    @Prop({ default: 10 }) readonly itemsPerPage: number;

    /**
     * getters, setters
     */
    get selected() {
        return this.value;
    }
    set selected(newValue: Array<any>) {
        this.$emit('input', newValue);
    }
    get itemColSlotNames(): Array<string> {
        const slots = this.$scopedSlots;
        if (!slots) {
            return [];
        }
        return Object.keys(slots)
            .filter(key => key.startsWith('item.'))
            .filter(key => key !== 'item.data-table-select');
    }
    get headerColSlotNames(): Array<string> {
        const slots = this.$scopedSlots;
        if (!slots) {
            return [];
        }
        return Object.keys(slots)
            .filter(key => key.startsWith('header.'))
            .map(key => key.replace(/^header\./, ''))
            .filter(key => key !== 'data-table-select');
    }
    get hasHeaderMenuButtonSlot(): boolean {
        const slots = this.$scopedSlots;
        if (!slots) {
            return false;
        }
        // data-tableタグ内のv-slotを調べて、
        // チェックボックス横メニューボタン用の'header-menu-button'があればtrue
        return Object.keys(slots).includes('header-menu-button');
    }
    get hasItemSlot(): boolean {
        const slots = this.$scopedSlots;
        if (!slots) {
            return false;
        }
        // data-tableタグ内のv-slotを調べて、
        // 行全体用の'item'があればtrue
        return Object.keys(slots).includes('item');
    }

    /**
     * methods
     */
    updateSortBy<T>(column: T): void {
        if (column !== this.sort.column) {
            const newValue = new SortParam<T>();
            newValue.column = column;
            newValue.direction = 'asc';
            this.$emit('update:sort', newValue);
        }
    }
    updateSortDesc<T>(desc: boolean): void {
        if (desc !== (this.sort.direction === 'desc')) {
            const newValue = new SortParam<T>();
            newValue.column = this.sort.column;
            newValue.direction = desc ? 'desc' : 'asc';
            this.$emit('update:sort', newValue);
        }
    }
    onClickRow(item: any, { select, isSelected }): void {
        if (this.disableSelect) {
            return;
        }
        select(!isSelected);
    }
    isItemSelectable(item: any): boolean {
        if (item[this.selectableKey] === undefined) {
            return true;
        }
        return !!item[this.selectableKey];
    }
}
</script>

<style lang="scss" scoped>
.DataTable {
    &__TableHeader {
        :deep(.v-icon.v-data-table-header__icon) {
            color: white !important;
        }
    }
    :deep(table) {
        table-layout: fixed;
    }
    :deep(.v-data-table__wrapper table thead tr th) {
        font-size: 0.87rem;
    }
    :deep(.v-data-table__wrapper table tbody tr td) {
        font-size: 0.75rem;
        padding-top: 0.5em;
        padding-bottom: 0.5em;
    }
    :deep(.v-icon.v-icon) {
        font-size: 1rem;
    }
    :deep(.v-toolbar__content) {
        padding-left: 0em;
    }
}
</style>
